import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

/**
 * WHICH TIER, AT WHICH COMPANY, AM I ACTING AS?
 *
 * ── What changed from v1, and why ──────────────────────────────────────────
 *
 * v1 held a `mode` — coordinator or partner — which was only a real choice for
 * a `hybrid` company. Under the spine a company can hold several tiers, so
 * "which mode am I in" and "which tier am I acting as" turned out to be the
 * same question asked twice. `hybrid` is not a third kind of business; it is a
 * business standing in two places, and it becomes two grants.
 *
 * So: `mode` → `tier`, two values → five.
 *
 * ── The rule this file exists to enforce ───────────────────────────────────
 *
 *     localStorage PROPOSES · Redux HOLDS · the server DISPOSES.
 *
 * localStorage stores a PREFERENCE, never a permission. A stale entry can only
 * ask for something; it can never grant it. Every hydration re-validates the
 * remembered choice against `actable_tiers` — which the server computed — and
 * discards it if the grant has gone. That is why a revoked tier cannot leave a
 * selection behind that keeps working.
 *
 * ── Navigation is tier-driven ──────────────────────────────────────────────
 *
 * The active company decides the acting tenant and the ENTITLED TIER SET. The
 * active tier decides the RAIL. Membership authority decides which actions are
 * offered — never the rail's structure. There is deliberately no per-company
 * navigation state in here, and none should be added.
 */

export type TierCode = "owner" | "consultant" | "tier1" | "mep" | "operative";

/** A grant, as the server resolved it. company_id is null for a person-tier. */
export interface ActableTier {
  /** Demo mode only — the company does not really hold this. Labelled in the
   *  navigator so a demonstrator can always see what is genuinely bought. */
  via_demo?: boolean;
  tier_code: TierCode;
  company_id: string | null;
  is_primary: boolean;
  subject: "company" | "person";
  rank: number;
}

/** A row from the catalogue — every active tier, so locked bars can be drawn. */
export interface TierRow {
  code: TierCode;
  name: string;
  strapline: string | null;
  rank: number;
  subject: "company" | "person";
  fee_label: string | null;
  package_label: string | null;
  is_free: boolean;
  home_route: string | null;
}

export interface Membership {
  company_id: string;
  name: string | null;
  kind: "coordinator" | "partner" | "hybrid";
  role: string;
}

export interface CapabilityGrant {
  code: string;
  scope: "any" | "own";
}

export interface ActingState {
  /** null ONLY for a person-tier caller — an operative has no company. */
  companyId: string | null;
  tier: TierCode | null;
  memberships: Membership[];
  /** Every active tier. Drives the Spine modal's five bars. */
  catalogue: TierRow[];
  /** What this caller may act as. Drives which bars illuminate. */
  actable: ActableTier[];
  /** The caller's capabilities in the ACTING company. Presentation only. */
  capabilities: CapabilityGrant[];
  /**
   * Tier remembered PER COMPANY, not per user. Priya is at a coordinator and a
   * dual-tier company; one shared value would be wrong for one of them every
   * time she switched.
   */
  tierByCompany: Record<string, TierCode>;
  /** False until the server has answered. Nothing sends headers before this. */
  hydrated: boolean;
  /**
   * DEMO MODE, as reported by the server.
   *
   * NOT persisted to localStorage, and that is the point: it is a property of
   * the DEPLOYMENT, re-read on every hydration. Persisting it would let a
   * stale browser show demo controls against a production API, which is the
   * one way this feature could leak.
   */
  demoMode: boolean;
}

const STORAGE_KEY = "lc_acting_v2";
/** v1's key. Read once during migration, then removed. */
const LEGACY_KEY = "lc_acting_v1";

interface Persisted {
  companyId: string | null;
  tierByCompany: Record<string, TierCode>;
}

/** v1 stored a mode. These are the two tiers those modes became. */
const MODE_TO_TIER: Record<string, TierCode> = {
  coordinator: "tier1",
  partner: "mep",
};

function readPersisted(): Persisted {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const p = JSON.parse(raw) as Persisted;
      return {
        companyId: p.companyId ?? null,
        tierByCompany: p.tierByCompany ?? {},
      };
    }

    /* ── One-time migration from v1 ────────────────────────────────────────
       Someone mid-session has a v1 preference. Translating it means their
       remembered choice survives the upgrade instead of silently resetting to
       the primary tier — which would look like the app forgetting them. */
    const legacy = localStorage.getItem(LEGACY_KEY);
    if (legacy) {
      const v1 = JSON.parse(legacy) as {
        companyId: string | null;
        modeByCompany?: Record<string, string>;
      };
      const tierByCompany: Record<string, TierCode> = {};
      for (const [company, mode] of Object.entries(v1.modeByCompany ?? {})) {
        const tier = MODE_TO_TIER[mode];
        if (tier) tierByCompany[company] = tier;
      }
      return { companyId: v1.companyId ?? null, tierByCompany };
    }
  } catch {
    /* Private mode, cleared storage, corrupt JSON — all mean the same thing
       here: no preference. Never a reason to fail hydration. */
  }
  return { companyId: null, tierByCompany: {} };
}

function writePersisted(state: ActingState): void {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ companyId: state.companyId, tierByCompany: state.tierByCompany }),
    );
  } catch {
    /* A preference that cannot be saved is a worse session, not a broken one. */
  }
}

/**
 * Pick a tier for a company from what the server says is actable.
 *
 * Order: the remembered preference IF it is still granted, then the company's
 * primary, then its lowest-ranked grant. The first step is the one that
 * matters — it is where a revoked tier gets dropped.
 */
export function resolveTier(
  companyId: string | null,
  actable: ActableTier[],
  remembered: Record<string, TierCode>,
): TierCode | null {
  const forCompany = actable.filter((a) => a.company_id === companyId);
  if (forCompany.length === 0) return null;

  const want = companyId ? remembered[companyId] : undefined;
  if (want && forCompany.some((a) => a.tier_code === want)) return want;

  const primary = forCompany.find((a) => a.is_primary);
  return (primary ?? [...forCompany].sort((a, b) => a.rank - b.rank)[0]!).tier_code;
}

const persisted = readPersisted();

const initialState: ActingState = {
  companyId: null,
  tier: null,
  memberships: [],
  catalogue: [],
  actable: [],
  capabilities: [],
  tierByCompany: persisted.tierByCompany,
  hydrated: false,
  demoMode: false,
};

export interface HydratePayload {
  memberships: Membership[];
  tiers: TierRow[];
  actable_tiers: ActableTier[];
  capabilities: CapabilityGrant[];
  demo_mode?: boolean;
}

const actingSlice = createSlice({
  name: "acting",
  initialState,
  reducers: {
    /**
     * The server has answered. Reconcile the remembered preference against it.
     *
     * The order here is the security argument: the server's `actable` is read
     * FIRST and the stored choice is only honoured if it appears in it. A
     * grant revoked since the last visit simply is not in the list, so the
     * selection falls through to the primary rather than pointing at something
     * the next request would be refused for.
     */
    hydrate(state, action: PayloadAction<HydratePayload>) {
      const { memberships, tiers, actable_tiers, capabilities } = action.payload;
      /* Straight from the server every time. Never remembered. */
      state.demoMode = action.payload.demo_mode === true;
      state.memberships = memberships ?? [];
      state.catalogue = tiers ?? [];
      state.actable = actable_tiers ?? [];
      state.capabilities = capabilities ?? [];

      const stored = readPersisted();

      /* A person-tier holder (operative) has no company at all, and that is
         not a missing value — it is the defining property of the tier. They
         are checked for first because every company-shaped lookup below would
         return nothing for them. */
      const personGrant = state.actable.find((a) => a.subject === "person");
      const companyGrants = state.actable.filter((a) => a.company_id !== null);

      if (companyGrants.length === 0 && personGrant) {
        state.companyId = null;
        state.tier = personGrant.tier_code;
      } else {
        const remembered = companyGrants.some((a) => a.company_id === stored.companyId)
          ? stored.companyId
          : null;
        const primary = companyGrants.find((a) => a.is_primary);
        state.companyId =
          remembered ?? primary?.company_id ?? companyGrants[0]?.company_id ?? null;
        state.tier = resolveTier(state.companyId, state.actable, state.tierByCompany);
      }

      if (state.companyId && state.tier) {
        state.tierByCompany[state.companyId] = state.tier;
      }
      state.hydrated = true;
      writePersisted(state);

      /* v1's key has been folded in above. Leaving it would let two sources of
         truth drift apart, and the older one would eventually win somewhere. */
      try { localStorage.removeItem(LEGACY_KEY); } catch { /* ignore */ }
    },

    /** Switch company. The tier is re-derived — it belongs to the company. */
    setCompany(state, action: PayloadAction<string>) {
      const has = state.actable.some((a) => a.company_id === action.payload);
      if (!has) return;
      state.companyId = action.payload;
      state.tier = resolveTier(action.payload, state.actable, state.tierByCompany);
      if (state.tier) state.tierByCompany[action.payload] = state.tier;
      writePersisted(state);
    },

    /**
     * Switch tier, optionally at a different company — the Spine modal picks a
     * BAR, and a bar is a (tier, company) pair.
     *
     * Silently ignores a grant the caller does not hold. The modal only offers
     * illuminated bars, so reaching here with an unheld tier means state has
     * drifted — and refusing quietly is better than throwing, because the
     * server would refuse the next request anyway and the UI re-hydrates.
     */
    setTier(
      state,
      action: PayloadAction<{ tier: TierCode; companyId?: string | null }>,
    ) {
      const { tier } = action.payload;
      const companyId =
        action.payload.companyId !== undefined ? action.payload.companyId : state.companyId;

      const grant = state.actable.find(
        (a) => a.tier_code === tier && a.company_id === companyId,
      );
      if (!grant) return;

      state.companyId = grant.company_id;
      state.tier = grant.tier_code;
      if (grant.company_id) state.tierByCompany[grant.company_id] = grant.tier_code;
      writePersisted(state);
    },

    /** Sign-out. Clears the selection, keeps the per-company preferences. */
    reset(state) {
      state.companyId = null;
      state.tier = null;
      state.memberships = [];
      state.catalogue = [];
      state.actable = [];
      state.capabilities = [];
      state.hydrated = false;
      state.demoMode = false;
    },
  },
});

export const { hydrate, setCompany, setTier, reset } = actingSlice.actions;
export default actingSlice.reducer;
